import React, { useState, useEffect  } from 'react';
import ReactDOM from 'react-dom'; // Import ReactDOM since it's bundled
import './StupidSalesWidget.css';  // Add this line

import Field from './Field'; // Import the Field componen

export const StupidSalesWidget = ({ config }) => {
  const [hostStatus, setHostStatus] = useState('checking');
  const [currentHost, setCurrentHost] = useState(null);
  const [companyCalendly, setCompanyCalendly] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [widgetFields, setWidgetFields] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: ''
  });
  const [countdown, setCountdown] = useState(60);
  const [hostJoined, setHostJoined] = useState(false);
  const [showCountdown, setShowCountdown] = useState(false);
  const [showApology, setShowApology] = useState(false);
  const defaultMessage = `I'm free to chat now 😊`;
  const [greetingsMessage, setGreetingsMessage] = useState(config.greetingsMessage || defaultMessage);

  const [formErrors, setFormErrors] = useState({
    email: '',
    phone: '',
  });

  // Regular expressions for validation
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const phoneRegex = /^\+?[\d\s\-()]{7,}$/;

  // Add theme validation at the start
  const theme = {
    primaryColor: config.theme?.primaryColor || '#33D298',
    primaryHoverColor: config.theme?.primaryHoverColor || '#2BB583',
    spinnerColor: config.theme?.spinnerColor || '#33D298'
  };

  // Email validation function
  const validateEmail = (email) => {
    if (!emailRegex.test(email)) {
      setFormErrors((prevErrors) => ({
        ...prevErrors,
        email: 'Please enter a valid email address.',
      }));
      return false;
    } else {
      setFormErrors((prevErrors) => ({
        ...prevErrors,
        email: '',
      }));
      return true;
    }
  };

  // Phone number validation function
  const validatePhone = (phone) => {
    if (phone && !phoneRegex.test(phone)) {
      setFormErrors((prevErrors) => ({
        ...prevErrors,
        phone: 'Please enter a valid phone number.',
      }));
      return false;
    } else {
      setFormErrors((prevErrors) => ({
        ...prevErrors,
        phone: '',
      }));
      return true;
    }
  };
  
  useEffect(() => {
    const initializeWidget = async () => {
      if (!config.companyId || !config.apiKey) {
        console.error('Missing required configuration');
        setHostStatus('unavailable');
        setIsLoading(false);
        return;
      }

      if (config.greetingsMessage) {
        setGreetingsMessage(config.greetingsMessage);
      } else {
        setGreetingsMessage(defaultMessage);
      }

      try {
        // Initial status check
        await checkHostStatus();
        // Setup WebSocket for real-time updates
        setupWebSocket();
      } catch (error) {
        console.error('Error initializing widget:', error);
        setHostStatus('unavailable');
      } finally {
        setIsLoading(false);
      }
    };

    initializeWidget();
  }, [config, defaultMessage]);

  useEffect(() => {
    // Initialize widgetFields using getWidgetFields
    const initializeFields = async () => {
      try {
        await getWidgetFields();
        // Removed logging widgetFields here
      } catch (error) {
        console.error('Error fetching widget fields:', error);
      }
    };

    initializeFields();
  }, []);

  const getWidgetFields = async () => {
    try {
      const response = await fetch(
        `${config.backendUrl}/api/widget-fields/${config.companyId}`,
        {
          headers: {
            'x-api-key': config.apiKey,
            'Content-Type': 'application/json'
          }
        }
      );
      const data = await response.json();
      // Log the raw data
      // If data is an object containing fields array
      // setWidgetFields(data.fields);
      // Else, if data is directly the fields array
      setWidgetFields(data.widget_fields);
    } catch (error) {
      console.log('Fetch error:', error.message);
    }
  };

  useEffect(() => {
    // Set CSS variables based on config colors
    const root = document.documentElement;
    root.style.setProperty('--primary-color', theme.primaryColor);
    root.style.setProperty('--primary-hover-color', theme.primaryHoverColor);
    root.style.setProperty('--primary-shadow', `${theme.primaryColor}40`);
  }, [theme]);

  const checkHostStatus = async () => {
    try {
      const backendUrl = config.backendUrl; // Use local variable
      const response = await fetch(
        `${backendUrl}/api/host-status/${config.companyId}`,
        {
          headers: {
            'x-api-key': config.apiKey,
            'Content-Type': 'application/json'
          }
        }
      );
      
      const data = await response.json();
      setHostStatus(data.status);

      if (data.status === 'online') {
        setCurrentHost(data.host);
      } else {
        // Set Calendly URL for both offline and unavailable states
        setCompanyCalendly(data.company_calendly_url || data.calendlyUrl);
      }
      setIsLoading(false);
    } catch (error) {
      console.error('Error checking host status:', error);
      setHostStatus('unavailable');
      // Also try to set Calendly URL on error if available
      if (error.response?.data?.company_calendly_url) {
        setCompanyCalendly(error.response.data.company_calendly_url);
      }
      setIsLoading(false);
    }
  };

  const setupWebSocket = () => {
    const wsUrl = config.backendUrl.replace('http', 'ws');
    
    const ws = new WebSocket(wsUrl);

    ws.onopen = () => {
 
      ws.send(JSON.stringify({
        type: 'register',
        companyId: config.companyId,
        apiKey: config.apiKey
      }));
    };

    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
    
      if (data.type === 'status_update' && data.companyId === config.companyId) {
        setHostStatus(data.status);
        if (data.status === 'online') {
          setCurrentHost(data.host);
        } else {
          setCompanyCalendly(data.calendlyUrl);
        }
      }
    };

    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
    };

    return () => {
      ws.close();
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const isEmailValid = validateEmail(formData.email);
    const isPhoneValid = validatePhone(formData.phone);

    // If there are validation errors, do not proceed
    if (!isEmailValid || !isPhoneValid) {
      return;
    }
    
    setIsSubmitting(true);
    setShowCountdown(true); // Start countdown when form is submitted

    try {
      const response = await fetch(`${config.backendUrl}/api/send-notifications`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': config.apiKey
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          company: formData.company,
          phone: formData.phone,
          companyId: config.companyId
        })
      });
  
      const data = await response.json();
      if (data.success && data.roomUrl) {
        setHostJoined(true);
        location.assign(data.roomUrl);
      } else {
        throw new Error(data.error || 'Failed to start video chat');
      }
    } catch (error) {
      console.error('Error:', error);
      setShowApology(true);
      setShowCountdown(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Add countdown effect
  useEffect(() => {
    if (!showCountdown) return;

    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          if (!hostJoined) {
            setShowApology(true);
            setShowCountdown(false);
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [showCountdown, hostJoined]);

  useEffect(() => {
    if (!companyCalendly) return; // Don't load if no URL

    // Load Calendly script dynamically
    const script = document.createElement('script');
    script.src = 'https://assets.calendly.com/assets/external/widget.js';
    script.async = true;
    document.body.appendChild(script);

    script.onload = () => {
      if (window.Calendly) {
        // Remove any existing widgets
        const existingWidgets = document.querySelectorAll('.calendly-inline-widget');
        existingWidgets.forEach(widget => {
          widget.innerHTML = ''; // Clear existing content
        });

        // Initialize only if we have a parent element
        const parentElement = document.querySelector('.calendly-inline-widget');
        if (parentElement) {
          window.Calendly.initInlineWidget({
            url: companyCalendly,
            parentElement: parentElement
          });
        }
      }
    };

    return () => {
      // Cleanup on component unmount
      const script = document.querySelector('script[src="https://assets.calendly.com/assets/external/widget.js"]');
      if (script) {
        script.remove();
      }
    };
  }, [companyCalendly]);

  // First check if we're still checking status or waiting for calendlyUrl
  if (hostStatus === 'checking' || isLoading) {
    return (
      <div className="widget-loading">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  // If online, show the chat form
  //TODO add handling empty widgetFields
  if (hostStatus === 'online') {
    return (
      <div className="stupid-sales-widget">
        <div className="chat-form-container">
          {currentHost && (
            <div className="host-info">
              <div className="host-headshot-container">
                {currentHost.headshot_url && (
                  <img 
                    src={currentHost.headshot_url} 
                    alt={`${currentHost.name}'s profile`} 
                    className="host-headshot"
                  />
                )}
                <div className="live-indicator">
                  <span className="live-dot"></span>
                  LIVE NOW
                </div>
              </div>
              <div className="host-text">
                <h3>I'm {currentHost.name}! {greetingsMessage}</h3>
              </div>
            </div>
          )}
          <form className="chat-form" onSubmit={handleSubmit}>
          {widgetFields.map((field) => (
              <Field
                key={field.name}
                config={field}
                value={formData[field.name] || ''}
                onChange={handleInputChange}
                error={formErrors[field.name]}
              />
            ))}
            <button 
              type="submit" 
              className="submit-button" 
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <div className="loading-spinner"></div>
              ) : (
                'Start Video Chat'
              )}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // If unavailable with Calendly URL, show Calendly
  if (companyCalendly) {
    return (
      <div className="stupid-sales-widget">
        <div className="calendly-container">
          <div 
            className="calendly-inline-widget"
            style={{ 
              minWidth: '320px',
              height: '600px',
              overflow: 'hidden'
            }}
          />
        </div>
      </div>
    );
  }

  // If unavailable without Calendly URL, show apology
  return (
    <div className="stupid-sales-widget">
      <div className="unavailable-message">
        <h3>We're not available right now</h3>
        <p>Sorry, we're currently unavailable. Please try again later.</p>
      </div>
    </div>
  );
};

// For non-React usage (standalone script)
const init = (selector, config) => {
  // Validate required config
  if (!config.companyId || !config.apiKey || !config.backendUrl) {
    throw new Error('Missing required configuration (companyId, apiKey, or backendUrl)');
  }

  const container = document.querySelector(selector);
  if (!container) {
    throw new Error(`Container element "${selector}" not found`);
  }

  // Create a div for React to render into
  const widgetRoot = document.createElement('div');
  
  // Apply theme CSS variables
  if (config.theme) {
    widgetRoot.style.setProperty('--ssw-primary-color', config.theme.primaryColor);
    widgetRoot.style.setProperty('--ssw-primary-hover-color', config.theme.primaryHoverColor);
    widgetRoot.style.setProperty('--ssw-spinner-color', config.theme.spinnerColor);
  }

  container.appendChild(widgetRoot);
  ReactDOM.render(<StupidSalesWidget config={config} />, widgetRoot);
};

// Export everything as a single object
export default {
  init,
  StupidSalesWidget
}; 